import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { ChevronLeft, ChevronRight, X, Image as ImageIcon, Loader2 } from "lucide-react";

const CATEGORIES = [
  { id: "all", label: "Alle" },
  { id: "bygningsrenovering", label: "Bygningsrenovering" },
  { id: "nybyggeri", label: "Nybyggeri" },
  { id: "tagarbejde", label: "Tagarbejde" },
  { id: "traekonstruktioner", label: "Trækonstruktioner" },
  { id: "doere-og-vinduer", label: "Døre og Vinduer" }
];

export default function Gallery() {
  const [activeCategory, setActiveCategory] = useState("all");
  const [galleryItems, setGalleryItems] = useState([]);
  const [projectItems, setProjectItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    async function fetchAllImages() {
      setLoading(true);
      try {
        const [galleryRes, projectsRes] = await Promise.all([
          fetch("https://api.dev.auxo.dk/public/v2/content/gallery?limit=100"),
          fetch("https://api.dev.auxo.dk/public/v2/content/projects?limit=100")
        ]);

        let galleryData = [];
        let projectsData = [];

        if (galleryRes.ok) {
          const data = await galleryRes.json();
          galleryData = data.items || [];
        }
        if (projectsRes.ok) {
          const data = await projectsRes.json();
          projectsData = data.items || [];
        }

        setGalleryItems(galleryData);
        setProjectItems(projectsData);
      } catch (err) {
        console.error("Fejl ved hentning af billeder:", err);
      } finally {
        setLoading(false);
      }
    }

    fetchAllImages();
  }, []);

  // Helper to check if an image is AI-generated
  const isAiImage = (url) => {
    if (!url) return true;
    const lowercaseUrl = url.toLowerCase();
    return (
      lowercaseUrl.includes("tagarbejde.jpg") ||
      lowercaseUrl.includes("bygningsrenovering.jpg") ||
      lowercaseUrl.includes("nybyggeri.jpg") ||
      lowercaseUrl.includes("traekonstruktioner.jpg") ||
      lowercaseUrl.includes("doere-og-vinduer.jpg") ||
      lowercaseUrl.includes("tagarbejde-2.jpg") ||
      lowercaseUrl.includes("bygningsrenovering-2.jpg") ||
      lowercaseUrl.includes("nybyggeri-2.jpg") ||
      lowercaseUrl.includes("traekonstruktioner-2.jpg") ||
      lowercaseUrl.includes("doere-og-vinduer-2.jpg") ||
      lowercaseUrl.includes("hero-byggeri.jpg")
    );
  };

  // Build the list of all images from all sources
  const allImages = [];

  // 1. Add images from CMS gallery collection
  galleryItems.forEach((item) => {
    const title = item.title?.da || item.title || "Byggeprojekt";
    const categorySlug = item.terms?.["byggeri-category"]?.[0] || "";
    const categoryLabel = CATEGORIES.find((c) => c.id === categorySlug)?.label || "Andet";

    const images = Array.isArray(item.image) ? item.image : (item.image ? [item.image] : []);
    images.forEach((imgUrl, idx) => {
      if (!isAiImage(imgUrl)) {
        allImages.push({
          id: `gallery-${item.id}-${idx}`,
          imageUrl: imgUrl,
          title,
          categoryLabel,
          categorySlug
        });
      }
    });
  });

  // 2. Add images from CMS projects collection
  projectItems.forEach((item) => {
    const title = item.title?.da || item.title || "Byggeprojekt";
    const categorySlug = item.terms?.["byggeri-category"]?.[0] || "";
    const categoryLabel = CATEGORIES.find((c) => c.id === categorySlug)?.label || "Andet";

    const images = Array.isArray(item.image) ? item.image : (item.image ? [item.image] : []);
    images.forEach((imgUrl, idx) => {
      if (!isAiImage(imgUrl)) {
        allImages.push({
          id: `project-${item.id}-${idx}`,
          imageUrl: imgUrl,
          title,
          categoryLabel,
          categorySlug
        });
      }
    });
  });

  // 3. Add static images from the site
  const staticImages = [
    {
      id: "static-hero",
      imageUrl: "https://assets.auxodev.dk/assets/531e2906-0b4f-4a2d-bfb4-6b0bf7bd2bc6/image00005.jpeg",
      title: "Opførelse af moderne parcelhus",
      categorySlug: "nybyggeri",
      categoryLabel: "Nybyggeri"
    },
    {
      id: "static-intro-1",
      imageUrl: "https://assets.auxodev.dk/assets/531e2906-0b4f-4a2d-bfb4-6b0bf7bd2bc6/image00025.jpeg",
      title: "Murerarbejde og renovering",
      categorySlug: "bygningsrenovering",
      categoryLabel: "Bygningsrenovering"
    },
    {
      id: "static-intro-2",
      imageUrl: "https://assets.auxodev.dk/assets/531e2906-0b4f-4a2d-bfb4-6b0bf7bd2bc6/image00025.jpeg",
      title: "Kvalitetsbyggeri i Janderup",
      categorySlug: "bygningsrenovering",
      categoryLabel: "Bygningsrenovering"
    },
    {
      id: "static-about-1",
      imageUrl: "https://assets.auxodev.dk/assets/531e2906-0b4f-4a2d-bfb4-6b0bf7bd2bc6/image00018.jpeg",
      title: "Moderne funkisvilla under opførelse",
      categorySlug: "nybyggeri",
      categoryLabel: "Nybyggeri"
    },
    {
      id: "static-about-2",
      imageUrl: "https://assets.auxodev.dk/assets/531e2906-0b4f-4a2d-bfb4-6b0bf7bd2bc6/image00013.jpeg",
      title: "Nyopført parcelhus i Janderup",
      categorySlug: "nybyggeri",
      categoryLabel: "Nybyggeri"
    }
  ];

  staticImages.forEach((img) => {
    if (!isAiImage(img.imageUrl)) {
      allImages.push(img);
    }
  });

  // Deduplicate images by their base UUID/filename
  const uniqueImages = [];
  const seenBases = new Set();

  allImages.forEach((img) => {
    if (!img.imageUrl) return;
    const filename = img.imageUrl.split("/").pop();
    const baseName = filename.replace(/(_\d+)?\.[a-zA-Z0-9]+$/, "").toLowerCase();
    
    if (!seenBases.has(baseName)) {
      seenBases.add(baseName);
      uniqueImages.push(img);
    }
  });

  // Filter based on activeCategory
  const filteredImages = activeCategory === "all"
    ? uniqueImages
    : uniqueImages.filter((img) => img.categorySlug === activeCategory);

  // Lightbox navigation
  const handlePrev = (e) => {
    e.stopPropagation();
    if (lightboxIndex === null || filteredImages.length === 0) return;
    setLightboxIndex((prev) => (prev === 0 ? filteredImages.length - 1 : prev - 1));
  };

  const handleNext = (e) => {
    e.stopPropagation();
    if (lightboxIndex === null || filteredImages.length === 0) return;
    setLightboxIndex((prev) => (prev === filteredImages.length - 1 ? 0 : prev + 1));
  };

  // Keyboard support for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (lightboxIndex === null) return;
      if (e.key === "ArrowLeft") handlePrev(e);
      if (e.key === "ArrowRight") handleNext(e);
      if (e.key === "Escape") setLightboxIndex(null);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxIndex, filteredImages]);

  // Helper to get Cloudflare optimized image URL
  function getResizedImageUrl(url, width = 600, quality = 80) {
    if (!url) return "";
    if (!url.includes("assets.auxodev.dk")) return url;
    if (url.includes("/cdn-cgi/image/")) return url;
    
    return url.replace(
      "https://assets.auxodev.dk/assets/",
      `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=${quality},format=auto/assets/`
    );
  }

  return (
    <>
      <Helmet>
        <title>Projektgalleri - Henneberg Byggeri ApS</title>
        <meta name="description" content="Udforsk vores projektgalleri og se eksempler på vores professionelle tømrer- og murerarbejde i højeste kvalitet." />
        <meta property="og:title" content="Projektgalleri - Henneberg Byggeri ApS" />
        <meta property="og:description" content="Udforsk vores projektgalleri og se eksempler på vores professionelle tømrer- og murerarbejde i højeste kvalitet." />
        <link rel="canonical" href="https://hennebergbyggeri.dk/galleri" />
      </Helmet>

      {/* Hero Header */}
      <section className="relative bg-neutral-900 py-20 text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(194,166,121,0.08),transparent_70%)]" />
        <div className="relative mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
          <span className="text-xs font-bold uppercase tracking-widest text-[#C2A679]">
            Vores Håndværk
          </span>
          <h1 className="mt-3 text-4xl font-extrabold tracking-tight sm:text-5xl uppercase">
            Projektgalleri
          </h1>
          <div className="mx-auto mt-4 h-0.5 w-16 bg-[#C2A679]" />
          <p className="mx-auto mt-6 max-w-2xl text-base text-neutral-400 sm:text-lg leading-relaxed">
            Se et udvalg af vores færdiggjorte projekter. Vi leverer kompromisløst kvalitetshåndværk, der holder i mange år frem.
          </p>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="bg-white py-12 sm:py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          
          {/* Category Filter Menu */}
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-12">
            {CATEGORIES.map((category) => {
              const isActive = activeCategory === category.id;
              return (
                <button
                  key={category.id}
                  onClick={() => {
                    setActiveCategory(category.id);
                    setLightboxIndex(null);
                  }}
                  className={`px-5 py-2.5 text-xs sm:text-sm font-bold uppercase tracking-wider transition-all duration-300 border ${
                    isActive
                      ? "bg-neutral-900 text-white border-neutral-900 shadow-sm"
                      : "bg-white text-neutral-600 border-neutral-200 hover:text-neutral-900 hover:border-neutral-400"
                  }`}
                >
                  {category.label}
                </button>
              );
            })}
          </div>

          {/* Gallery Grid or States */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-24">
              <Loader2 className="h-10 w-10 animate-spin text-[#C2A679] mb-4" />
              <p className="text-sm text-neutral-500 font-medium">Henter galleribilleder...</p>
            </div>
          ) : filteredImages.length === 0 ? (
            <div className="text-center py-20 rounded-xl border border-dashed border-neutral-200 bg-neutral-50 max-w-2xl mx-auto">
              <ImageIcon className="mx-auto h-12 w-12 text-neutral-400 mb-4" />
              <h3 className="text-lg font-bold text-neutral-900 uppercase">Ingen billeder fundet</h3>
              <p className="mt-2 text-sm text-neutral-500 max-w-md mx-auto">
                Der er i øjeblikket ingen billeder i denne kategori. Vi uploader løbende nye billeder af vores arbejde.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {filteredImages.map((imgItem, index) => {
                const thumbUrl = getResizedImageUrl(imgItem.imageUrl, 500, 80);
                return (
                  <div
                    key={imgItem.id}
                    onClick={() => setLightboxIndex(index)}
                    className="group relative overflow-hidden bg-neutral-100 shadow-sm cursor-pointer aspect-[4/3] border border-neutral-200 transition-all duration-300 hover:shadow-md hover:border-neutral-300"
                  >
                    <img
                      src={thumbUrl}
                      alt={`${imgItem.title} - ${imgItem.categoryLabel}`}
                      className="h-full w-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                    {/* Hover Overlay */}
                    <div className="absolute inset-0 bg-neutral-900/40 opacity-0 transition-opacity duration-300 group-hover:opacity-100 flex flex-col justify-end p-4">
                      <span className="text-[10px] font-bold uppercase tracking-widest text-[#C2A679] mb-1">
                        {imgItem.categoryLabel}
                      </span>
                      <h3 className="text-sm font-bold text-white uppercase truncate">
                        {imgItem.title}
                      </h3>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Premium Lightbox */}
      {lightboxIndex !== null && filteredImages.length > 0 && (
        <div
          className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-neutral-950/95 p-4 sm:p-6 select-none"
          onClick={() => setLightboxIndex(null)}
        >
          {/* Top Bar */}
          <div className="absolute top-0 inset-x-0 h-16 flex items-center justify-between px-6 bg-gradient-to-b from-black/50 to-transparent text-white">
            <div className="flex flex-col">
              <span className="text-[10px] font-bold uppercase tracking-widest text-[#C2A679]">
                {filteredImages[lightboxIndex].categoryLabel}
              </span>
              <h4 className="text-sm font-bold uppercase tracking-wide truncate max-w-xs sm:max-w-md">
                {filteredImages[lightboxIndex].title}
              </h4>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-xs text-neutral-400 font-medium">
                {lightboxIndex + 1} / {filteredImages.length}
              </span>
              <button
                className="text-white hover:text-[#C2A679] transition-colors duration-200 p-1"
                onClick={() => setLightboxIndex(null)}
                aria-label="Luk galleri"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Main Image Container */}
          <div className="relative w-full max-w-5xl max-h-[75vh] flex items-center justify-center">
            {/* Left Navigation */}
            <button
              onClick={handlePrev}
              className="absolute left-2 sm:-left-16 z-10 p-3 rounded-full bg-black/40 hover:bg-black/70 text-white hover:text-[#C2A679] transition-all duration-200 focus:outline-none"
              aria-label="Forrige billede"
            >
              <ChevronLeft className="h-6 w-6 sm:h-8 sm:w-8" />
            </button>

            {/* Image */}
            <img
              src={getResizedImageUrl(filteredImages[lightboxIndex].imageUrl, 1200, 90)}
              alt={filteredImages[lightboxIndex].title}
              className="max-h-[75vh] max-w-full object-contain shadow-2xl border border-neutral-800"
              onClick={(e) => e.stopPropagation()}
            />

            {/* Right Navigation */}
            <button
              onClick={handleNext}
              className="absolute right-2 sm:-right-16 z-10 p-3 rounded-full bg-black/40 hover:bg-black/70 text-white hover:text-[#C2A679] transition-all duration-200 focus:outline-none"
              aria-label="Næste billede"
            >
              <ChevronRight className="h-6 w-6 sm:h-8 sm:w-8" />
            </button>
          </div>

          {/* Bottom Caption (Mobile Friendly) */}
          <div className="mt-4 text-center text-xs text-neutral-400 max-w-md px-4 sm:hidden">
            <p className="font-semibold text-white uppercase">{filteredImages[lightboxIndex].title}</p>
            <p className="mt-1">{filteredImages[lightboxIndex].categoryLabel}</p>
          </div>
        </div>
      )}
    </>
  );
}
